import type {
  CascadePreview,
  CascadeTenantPreview,
} from "@/features/identity-hub/api/identity-hub.api";
import { FEATURE_AREAS, type FeatureArea } from "@/features/identity-hub/lib/permission-catalog";
import { permissionTupleKey } from "@/features/identity-hub/hooks/usePermissionSelection";

type PermissionTuple = { type: string; method: string; route: string };

/** Where a permission tuple lives in the catalog. `groupLabel` is absent for page access. */
export interface CascadePermissionLabel {
  name: string;
  areaLabel: string;
  groupLabel?: string;
}

export type CascadeTenantChange = "new" | "updated" | "removed" | "failed";

/** Tenants the save will actually change — not `unchanged`, and not a display cap. */
export function affectedCascadeTenants(preview: CascadePreview): CascadeTenantPreview[] {
  return preview.tenants.filter(
    (row) =>
      row.diff.added.length + row.diff.removed.length > 0
      || row.status === "would-create"
      || row.status === "would-remove",
  );
}

export function cascadeTenantChange(row: CascadeTenantPreview): CascadeTenantChange {
  if (row.status === "would-create") return "new";
  if (row.status === "would-remove") return "removed";
  if (row.status === "failed") return "failed";
  return "updated";
}

const LABELS_BY_TUPLE: ReadonlyMap<string, CascadePermissionLabel> = (() => {
  const map = new Map<string, CascadePermissionLabel>();
  const visit = (areas: FeatureArea[]) => {
    for (const area of areas) {
      if (area.pagePerm) {
        const key = permissionTupleKey(area.pagePerm);
        if (!map.has(key)) map.set(key, { name: area.pagePerm.name, areaLabel: area.label });
      }
      for (const group of area.actionGroups ?? []) {
        for (const perm of group.perms) {
          const key = permissionTupleKey(perm);
          if (!map.has(key)) map.set(key, { name: perm.name, areaLabel: area.label, groupLabel: group.label });
        }
      }
      visit(area.children ?? []);
    }
  };
  visit(FEATURE_AREAS);
  return map;
})();

/** Catalog name and location for a stored permission tuple, or `null` when the catalog has no entry. */
export function describeCascadePermission(tuple: PermissionTuple): CascadePermissionLabel | null {
  return LABELS_BY_TUPLE.get(permissionTupleKey(tuple)) ?? null;
}
